import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Check, RotateCcw, FileText, Mail, PenLine, Bell, CalendarCheck, Timer, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge, ConfirmDelete, Empty, SelectField } from "./common";
import { InterventionDialog, OrderDialog, ReminderDialog } from "./forms";
import { SignatureDialog } from "./SignatureDialog";
import { NotificationsDialog } from "./NotificationsDialog";
import { NOTIF_KINDS, NOTIF_STATUS, NOTIF_STYLE, OUTLOOK_STATUS } from "@/lib/constants";
import { api, formatApiError, openInterventionPdf } from "@/lib/api";
import {
  INTERVENTION_STATUS, INTERVENTION_TYPES, ORDER_CATEGORIES, ORDER_STATUS, REMINDER_KINDS,
  fmtDate, fmtDateTime, fmtPrice, todayStr, canTime, WORKSHOP_STAGES,
} from "@/lib/constants";
import { ModeIcon } from "./ModeFields";

const useRowActions = (resource, reload) => {
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const patch = async (id, body, msg = "Mis à jour") => {
    try {
      await api.patch(`/${resource}/${id}`, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    await api.delete(`/${resource}/${del.id}`);
    toast.success("Supprimé");
    setDel(null);
    reload();
  };
  return { edit, setEdit, del, setDel, patch, remove };
};

const pdf = (id) => openInterventionPdf(id).catch((e) => toast.error(formatApiError(e)));

const TimerLink = ({ i, compact }) => canTime(i) && (
  <Button asChild size={compact ? "icon" : "default"} variant={compact ? "ghost" : "default"} className={compact ? "text-cyan-300" : "h-11"} title="Chronométrer">
    <Link to={`/interventions/${i.id}?chrono=1`} data-testid={`intervention-timer-${compact ? "" : "card-"}${i.id}`}><Timer className="h-4 w-4" />{!compact && <span className="ml-1">Chronométrer</span>}</Link>
  </Button>
);

const InterventionCards = ({ items, hideClient }) => (
  <ul className="space-y-2 lg:hidden" data-testid="interventions-cards">
    {items.map((i) => (
      <li key={i.id} className="rounded-xl border border-white/[0.07] bg-[#122549]/60 p-3" data-testid={`intervention-card-${i.id}`}>
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono-dt text-xs text-cyan-300">{fmtDateTime(i.scheduled_at)}</span>
          <span className="flex flex-wrap justify-end gap-1">
            {i.draft && <Badge value="Brouillon" style="a_planifier" />}
            {i.late && <Badge value="En retard" style="overdue" />}
            <Badge value={i.status} map={INTERVENTION_STATUS} />
          </span>
        </div>
        <p className="mt-1 flex items-center gap-1.5 font-medium text-white"><ModeIcon item={i} />{i.title || "(sans objet)"}</p>
        {!hideClient && <p className="text-sm text-slate-400">{i.client_name || "—"}</p>}
        <div className={`mt-3 grid gap-2 ${canTime(i) ? "grid-cols-2" : "grid-cols-1"}`}>
          <Button asChild variant="outline" className="h-11"><Link to={`/interventions/${i.id}`} data-testid={`intervention-card-open-${i.id}`}>Ouvrir la fiche<ChevronRight className="ml-1 h-4 w-4" /></Link></Button>
          <TimerLink i={i} />
        </div>
      </li>
    ))}
  </ul>
);

const RowButtons = ({ item, a, prefix, children }) => (
  <div className="flex justify-end gap-1">
    {children}
    <Button size="icon" variant="ghost" onClick={() => a.setEdit(item)} data-testid={`${prefix}-edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>
    <Button size="icon" variant="ghost" onClick={() => a.setDel(item)} data-testid={`${prefix}-delete-${item.id}`} className="hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
  </div>
);

const Wrap = ({ children }) => <div className="dt-card overflow-x-auto">{children}</div>;
const ClientLink = ({ id, name }) => (id ? <Link to={`/clients/${id}`} className="text-cyan-300 hover:underline">{name}</Link> : <span className="text-slate-500">—</span>);

export const InterventionsTable = ({ items, reload, hideClient }) => {
  const a = useRowActions("interventions", reload);
  const [sign, setSign] = useState(null);
  const [notif, setNotif] = useState(null);
  const sendBon = async (id) => {
    try {
      await api.post(`/interventions/${id}/send-email`);
      toast.success("Bon d'intervention envoyé au client");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (!items.length) return <Empty testid="interventions-empty">Aucune intervention.</Empty>;
  return (
    <>
    <InterventionCards items={items} hideClient={hideClient} />
    <div className="dt-card hidden overflow-x-auto lg:block">
      <Table data-testid="interventions-table">
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Objet</TableHead>{!hideClient && <TableHead>Client</TableHead>}
          <TableHead>Type</TableHead><TableHead>Technicien</TableHead><TableHead className="w-40">Statut</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {items.map((i) => (
            <TableRow key={i.id} data-testid={`intervention-row-${i.id}`}>
              <TableCell className="whitespace-nowrap font-mono-dt text-xs">{fmtDateTime(i.scheduled_at)}</TableCell>
              <TableCell className="font-medium text-white">
                <ModeIcon item={i} className="mr-1.5 h-3.5 w-3.5" />
                <Link to={`/interventions/${i.id}`} className="hover:text-cyan-300 hover:underline" data-testid={`intervention-open-${i.id}`}>{i.title || "(sans objet)"}</Link>
                {i.draft && <span className="ml-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-xs text-amber-200" data-testid={`intervention-draft-${i.id}`}>Brouillon</span>}
                {i.late && <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] uppercase text-red-200" data-testid={`intervention-late-${i.id}`}>En retard</span>}
                {i.signed_at && <span className="mt-0.5 flex items-center gap-1 text-xs font-normal text-emerald-300" data-testid={`intervention-signed-${i.id}`}><PenLine className="h-3 w-3" />Signé (client) par {i.signature_name}</span>}
                {i.tech_signed_at && <span className="flex items-center gap-1 text-xs font-normal text-emerald-300" data-testid={`intervention-tech-signed-${i.id}`}><PenLine className="h-3 w-3" />Signé (technicien) par {i.tech_signature_name}</span>}
                {i.bon_sent_at && <span className="flex items-center gap-1 text-xs font-normal text-cyan-300" data-testid={`intervention-bon-sent-${i.id}`}><Mail className="h-3 w-3" />Bon envoyé le {fmtDate(i.bon_sent_at)}</span>}
                {i.last_notification && <span className={`block text-xs font-normal ${NOTIF_STYLE[i.last_notification.status] || "text-slate-400"}`} data-testid={`intervention-notif-status-${i.id}`}>E-mail {(NOTIF_KINDS[i.last_notification.kind] || "").toLowerCase()} : {NOTIF_STATUS[i.last_notification.status] || i.last_notification.status}</span>}
                {i.outlook_sync_status && <span className="flex items-center gap-1 text-xs font-normal text-sky-300" data-testid={`intervention-outlook-status-${i.id}`}><CalendarCheck className="h-3 w-3" />{OUTLOOK_STATUS[i.outlook_sync_status] || i.outlook_sync_status}</span>}
              </TableCell>
              {!hideClient && <TableCell><ClientLink id={i.client_id} name={i.client_name} /></TableCell>}
              <TableCell className="whitespace-nowrap">{INTERVENTION_TYPES[i.type]}</TableCell>
              <TableCell className="text-slate-400">{i.technician_name || "—"}</TableCell>
              <TableCell>{i.mode === "atelier" ? <span className="text-xs text-slate-300" data-testid={`intervention-status-${i.id}`}>{INTERVENTION_STATUS[i.status]}<span className="block text-slate-500">{WORKSHOP_STAGES[i.workshop?.stage]}</span></span>
                : <SelectField className="h-8 text-xs" value={i.status} options={INTERVENTION_STATUS} testid={`intervention-status-${i.id}`} onChange={(v) => a.patch(i.id, { status: v }, "Statut mis à jour")} />}</TableCell>
              <TableCell>
                <RowButtons item={i} a={a} prefix="intervention">
                  <TimerLink i={i} compact />
                  <Button size="icon" variant="ghost" title="E-mails client" onClick={() => setNotif(i)} data-testid={`intervention-notifications-${i.id}`}><Bell className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" title="Signatures du bon d’intervention" aria-label="Signatures du bon d’intervention" onClick={() => setSign(i)} data-testid={`intervention-sign-${i.id}`}><PenLine className="h-4 w-4" /></Button>
                  {i.signed_at && <Button size="icon" variant="ghost" title="Envoyer le bon signé au client" onClick={() => sendBon(i.id)} data-testid={`intervention-send-bon-${i.id}`}><Mail className="h-4 w-4" /></Button>}
                  <Button size="icon" variant="ghost" title="Bon d'intervention PDF" onClick={() => pdf(i.id)} data-testid={`intervention-pdf-${i.id}`}><FileText className="h-4 w-4" /></Button>
                </RowButtons>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
      <InterventionDialog open={!!a.edit} onOpenChange={(o) => !o && a.setEdit(null)} initial={a.edit || {}} onSaved={reload} />
      <SignatureDialog intervention={sign} onOpenChange={(o) => !o && setSign(null)} onSaved={reload} />
      <ConfirmDelete open={!!a.del} onOpenChange={(o) => !o && a.setDel(null)} onConfirm={a.remove} label="cette intervention"
        description={a.del?.outlook_event_id ? "Cette action est définitive. Le rendez-vous lié sera aussi supprimé de votre calendrier Outlook et le client recevra une annulation si une confirmation lui avait été envoyée." : undefined} />
      <NotificationsDialog intervention={notif} onOpenChange={(o) => !o && setNotif(null)} onChanged={reload} />
    </>
  );
};

export const OrdersTable = ({ items, reload, hideClient }) => {
  const a = useRowActions("orders", reload);
  if (!items.length) return <Empty testid="orders-empty">Aucune commande.</Empty>;
  return (
    <Wrap>
      <Table data-testid="orders-table">
        <TableHeader><TableRow>
          <TableHead>Matériel</TableHead><TableHead>Catégorie</TableHead>{!hideClient && <TableHead>Client</TableHead>}
          <TableHead>Fournisseur</TableHead><TableHead>Total HT</TableHead><TableHead>Livraison</TableHead><TableHead className="w-40">État</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {items.map((o) => (
            <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
              <TableCell className="font-medium text-white">{o.quantity} × {o.item}{o.reference && <span className="block font-mono-dt text-xs text-slate-500">{o.reference}</span>}</TableCell>
              <TableCell>{ORDER_CATEGORIES[o.category]}</TableCell>
              {!hideClient && <TableCell>{o.client_id ? <ClientLink id={o.client_id} name={o.client_name} /> : <span className="text-slate-500">Stock</span>}</TableCell>}
              <TableCell className="text-slate-400">{o.supplier || "—"}</TableCell>
              <TableCell className="font-mono-dt text-xs">{o.unit_price == null ? "—" : fmtPrice(o.unit_price * o.quantity)}</TableCell>
              <TableCell className="whitespace-nowrap text-xs">{fmtDate(o.expected_date)}</TableCell>
              <TableCell><SelectField className="h-8 text-xs" value={o.status} options={ORDER_STATUS} testid={`order-status-${o.id}`} onChange={(v) => a.patch(o.id, { status: v }, "État mis à jour")} /></TableCell>
              <TableCell><RowButtons item={o} a={a} prefix="order" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <OrderDialog open={!!a.edit} onOpenChange={(o) => !o && a.setEdit(null)} initial={a.edit || {}} onSaved={reload} />
      <ConfirmDelete open={!!a.del} onOpenChange={(o) => !o && a.setDel(null)} onConfirm={a.remove} label="cette commande" />
    </Wrap>
  );
};

export const RemindersList = ({ items, reload }) => {
  const a = useRowActions("reminders", reload);
  const today = todayStr();
  const sendNow = async (id) => {
    try {
      await api.post(`/reminders/${id}/send-email`);
      toast.success("Email envoyé au client");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (!items.length) return <Empty testid="reminders-empty">Aucun rappel.</Empty>;
  return (
    <div className="space-y-3" data-testid="reminders-list">
      {items.map((r) => (
        <div key={r.id} data-testid={`reminder-row-${r.id}`} className="dt-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge value={r.kind} map={REMINDER_KINDS} />
              {!r.done && r.due_date < today && <Badge value="En retard" style="overdue" />}
              {r.recurrence_months > 0 && <span className="text-xs text-slate-400">↻ {r.recurrence_months} mois</span>}
              <span className="font-mono-dt text-xs text-slate-400">{fmtDate(r.due_date)}</span>
            </div>
            <p className={`mt-1 font-medium ${r.done ? "text-slate-500 line-through" : "text-white"}`}>{r.title}</p>
            {r.client_id && <ClientLink id={r.client_id} name={r.client_name} />}
            {r.notes && <p className="text-xs text-slate-400">{r.notes}</p>}
            {r.kind === "maintenance" && (
              <p className="mt-1 flex items-center gap-1 text-xs text-slate-400" data-testid={`reminder-email-status-${r.id}`}>
                <Mail className="h-3.5 w-3.5 text-cyan-300" />
                {r.email_sent_at ? `Email envoyé le ${fmtDate(r.email_sent_at)}` : r.notify_client ? `Email auto ${r.notify_days_before} j avant` : "Pas d'email auto"}
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-1">
            {r.done ? (
              <Button size="sm" variant="ghost" onClick={() => a.patch(r.id, { done: false }, "Rappel réouvert")} data-testid={`reminder-reopen-${r.id}`}><RotateCcw className="mr-1 h-4 w-4" />Rouvrir</Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => a.patch(r.id, { done: true }, "Rappel marqué comme fait")} data-testid={`reminder-mark-done-${r.id}`}><Check className="mr-1 h-4 w-4" />Fait</Button>
            )}
            {r.kind === "maintenance" && r.client_id && !r.done && (
              <Button size="sm" variant="ghost" onClick={() => sendNow(r.id)} data-testid={`reminder-send-email-${r.id}`}><Mail className="mr-1 h-4 w-4" />Envoyer</Button>
            )}
            <RowButtons item={r} a={a} prefix="reminder" />
          </div>
        </div>
      ))}
      <ReminderDialog open={!!a.edit} onOpenChange={(o) => !o && a.setEdit(null)} initial={a.edit || {}} onSaved={reload} />
      <ConfirmDelete open={!!a.del} onOpenChange={(o) => !o && a.setDel(null)} onConfirm={a.remove} label="ce rappel" />
    </div>
  );
};
